import type {Extension} from '@codemirror/state'
import {EditorView, ViewPlugin, ViewUpdate} from '@codemirror/view'

import {getAccurateCaretCoords} from '../utils'
import styles from './caret-flash.module.css'

/**
 * Creates a caret flash element positioned at the given coordinates with dynamic sizing
 */
// exported for testing purposes
export const createFlashElement = (x: number, y: number, fontSize: string | null): HTMLElement => {
  const element = document.createElement('div')
  element.className = styles.caretFlash || ''
  element.style.left = `${x}px`
  element.style.top = `${y}px`
  element.setAttribute('data-testid', 'caretFlash')

  // Set CSS var to dynamically size the flash based on font size
  element.style.setProperty('--text-font-size', fontSize)

  // Remove the element when animation completes
  element.addEventListener(
    'animationend',
    () => {
      element.remove()
    },
    {once: true},
  )

  return element
}

/**
 * Get the font size of the clicked text to help us size the flash
 */
// exported for testing purposes
export const getFontSize = (view: EditorView, coords: {left: number; top: number}): string | null => {
  const elementAtCursor = document.elementFromPoint(coords.left, coords.top)

  if (elementAtCursor && view.dom.contains(elementAtCursor)) {
    return window.getComputedStyle(elementAtCursor).fontSize
  }

  return null
}

function renderCaretFlash(view: EditorView, lastCaretPos: {x: number; y: number}) {
  const selection = view.state.selection.main

  // Text highlight is enough of a visual cue - no need to measure caret shift
  if (!selection.empty) return

  // We can't measure caret shift if this is null - bail out
  const coords = getAccurateCaretCoords(view, selection.head)
  if (!coords) return

  const newCaretPos = {x: coords.left + window.scrollX, y: coords.top + window.scrollY}

  // Calculate caret shift distance (hypotenuse accounts for x and y axis shift)
  const caretShiftDistance = Math.hypot(newCaretPos.x - lastCaretPos.x, newCaretPos.y - lastCaretPos.y)

  // Show flash only if significant movement occurred _and_ caret is not at line start.
  // 25px is a made up number. It just felt right when testing.
  if (newCaretPos.x > 0 && caretShiftDistance >= 25) {
    const fontSize = getFontSize(view, coords)
    const flashElement = createFlashElement(newCaretPos.x, newCaretPos.y, fontSize)
    document.body.appendChild(flashElement)
  }
}

/**
 * Use an update listener to track caret position changes,
 * but only when the selection changes
 */
const caretPosPlugin = ViewPlugin.fromClass(
  class {
    #lastStableCaretPos: {x: number; y: number} | null = null

    update(update: ViewUpdate) {
      if (!update.selectionSet) return

      const coords = getAccurateCaretCoords(update.view, update.state.selection.main.head)

      if (coords && coords.left > 0) {
        this.#lastStableCaretPos = {x: coords.left + window.scrollX, y: coords.top + window.scrollY}
      }
    }

    get lastStableCaretPos() {
      return this.#lastStableCaretPos
    }
  },
  {
    eventObservers: {
      click(this, event, view) {
        if (this.lastStableCaretPos) renderCaretFlash(view, this.lastStableCaretPos)
      },
    },
  },
)

/**
 * Plugin to handle mouse click events and trigger caret flash
 */
export const caretFlashExtension: Extension = caretPosPlugin
